import type {
	AnswerError, AnsweringFinishedEvent,
	AnsweringMessageUpdateEvent, AnsweringStartedEvent, AnsweringStepUpdateEvent,
	ChatMessage, Events, MessageSocketResponse
} from '@types'
import { useChatContext } from '@ui/chat'
import { useEffect, useState } from 'react'
import type { Socket } from 'socket.io-client'

interface MessageSocketProps {
	chatId: string;
	message: ChatMessage;
	socket: Socket;
}

export function useMessageSocket({ chatId, socket, message }: MessageSocketProps) {
	const { chatDispatch } = useChatContext()
	const [socketResponse, setSocketResponse] = useState<MessageSocketResponse>(message)

	useEffect(() => {
		if (!chatId || !socket || !message) {
			return
		}

		if (message.role === 'assistant' && message.status === 'processing') {
			setSocketResponse({ ...message, isThinking: true, answeringStep: { step: 'analyzing', status: 'pending' } })
		} else {
			setSocketResponse(message)
		}
	}, [message])

	useEffect(() => {
		if (!chatId || !socket || !message) {
			return
		}

		socket.on<Events>('answering:started', answerStarted)
		socket.on<Events>('answering:step-update', answerStepUpdate)
		socket.on<Events>('answering:message-update', answerMessageUpdate)
		socket.on<Events>('answering:finished', answerFinished)
		socket.on<Events>('answering:error', handleSendError)

		return (() => {
			socket.off('answering:started', answerStarted)
			socket.off('answering:step-update', answerStepUpdate)
			socket.off('answering:message-update', answerMessageUpdate)
			socket.off('answering:finished', answerFinished)
			socket.off('answering:error', handleSendError)
		})
	}, [socket])

	function isEventForThisMessage(event: {chatId: string; messageId?: string; message?: {id: string}}) {
		if (event.chatId !== chatId) {
			return false
		}

		const idToCheck = 'messageId' in event ? event.messageId : event.message?.id

		return idToCheck === message.id
	}

	function answerStarted(event: AnsweringStartedEvent) {
		if (!isEventForThisMessage(event)) {
			return
		}

		setSocketResponse({
			...event.message,
			content: event.message.content,
			selectedBrains: event.message.selectedBrains,
			contents: event.contents,
			isThinking: true,
			isAnswering: false,
			status: 'processing',
			answeringStep: { step: 'analyzing', status: 'pending' }
		})
	}

	function answerStepUpdate(event: AnsweringStepUpdateEvent) {
		if (!isEventForThisMessage(event)) {
			return
		}

		setSocketResponse(state => ({ ...state, answeringStep: event } satisfies MessageSocketResponse))
	}

	function answerMessageUpdate(event: AnsweringMessageUpdateEvent) {
		if (!isEventForThisMessage(event)) {
			return
		}

		const { content, thinkingContent, loadingArtifact, artifacts } = event

		setSocketResponse(state => ({
			...state,
			artifacts,
			content,
			thinkingContent,
			loadingArtifact,
			isThinking: false,
			isAnswering: true
		} satisfies MessageSocketResponse))
	}

	function answerFinished(event: AnsweringFinishedEvent) {
		if (!isEventForThisMessage(event)) {
			return
		}

		setSocketResponse(state => ({
			...state,
			content: event.message.content,
			contents: event.contents,
			answeringStep: undefined,
			sources: event.message.sources,
			isAnswering: false, isThinking: false, status: 'processed'
		} satisfies MessageSocketResponse))
		chatDispatch({ type: 'setIsThinkingOrAnswering', isThinkingOrAnswering: false })
	}

	function handleSendError(error?: AnswerError | undefined) {
		setSocketResponse(state => ({
			...state, isThinking: false, isAnswering: false, error, status: 'failed'
		} satisfies MessageSocketResponse))
	}

	return socketResponse
}
